import { supabase } from '@/lib/supabase'
import type {
  LocationFormData,
  LocationRow,
  LocationTableRow,
} from '@/types/locations'
import type { RecordStatusFilter } from '@/types/record-status'
import { applyRecordStatusFilter } from './helpers/apply-record-status-filter'
import { restoreRecord } from './helpers/restore-record'

/**
 * Fetch all locations for an organization
 */
export async function fetchLocations(
  organizationId: string,
  recordStatus: RecordStatusFilter = 'active'
): Promise<LocationTableRow[]> {
  let query = supabase
    .from('locations')
    .select('id, name, location_code, airport_code, city, state_province, status, created_at, deleted_at')
    .eq('organization_id', organizationId)

  query = applyRecordStatusFilter(query, recordStatus)

  const { data, error } = await query.order('name')

  if (error) throw error
  return data || []
}

/**
 * Fetch a single location by ID
 */
export async function fetchLocation(id: string): Promise<LocationRow> {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data
}

// Reserved slug values that conflict with app routes
const RESERVED_SLUGS = [
  'dashboard', 'clients', 'aircraft', 'hangars', 'units',
  'leases', 'transient', 'settings', 'admin', 'api', 'auth',
  'login', 'signup', 'billing', 'profile', 'reports', 'schedule',
  'fuel', 'contacts', 'locations', 'new', 'edit', 'delete',
]

/**
 * Generate a valid slug from a location code.
 * Ensures: starts with a letter, 2-50 chars, only lowercase alphanumeric + hyphens,
 * and not a reserved word.
 */
function generateSlug(code: string): string {
  let slug = code
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50)

  // Ensure slug starts with a letter (DB constraint: ^[a-z][a-z0-9-]*$)
  if (!slug || !/^[a-z]/.test(slug)) {
    slug = 'loc-' + slug
  }

  // Ensure minimum length of 2
  if (slug.length < 2) {
    slug = slug + '-1'
  }

  // Avoid reserved slugs by appending suffix
  if (RESERVED_SLUGS.includes(slug)) {
    slug = slug + '-1'
  }

  return slug.substring(0, 50)
}

/**
 * Create a new location
 */
export async function createLocation(
  organizationId: string,
  formData: LocationFormData
): Promise<LocationRow> {
  // Generate location_code: use provided value, fall back to airport_code, then generate unique
  const baseCode =
    formData.location_code?.trim().toUpperCase() ||
    formData.airport_code?.trim().toUpperCase() ||
    'LOC'

  // Append a short random suffix to avoid UNIQUE constraint violations on fallback codes
  const location_code = formData.location_code?.trim()
    ? baseCode
    : `${baseCode}-${Date.now().toString(36).slice(-4).toUpperCase()}`

  // Generate URL slug from location_code
  const slug = generateSlug(location_code)

  // Default coordinates (will be replaced by airport API lookup later)
  const latitude = formData.latitude ?? 0
  const longitude = formData.longitude ?? 0

  const { data, error } = await supabase
    .from('locations')
    .insert({
      organization_id: organizationId,
      name: formData.name,
      location_code,
      slug,
      airport_code: formData.airport_code?.toUpperCase() || null,
      iata_code: formData.iata_code?.toUpperCase() || null,
      latitude,
      longitude,
      timezone: formData.timezone || 'America/New_York',
      address_line1: formData.address_line1 || null,
      city: formData.city || null,
      state_province: formData.state_province || null,
      postal_code: formData.postal_code || null,
      country: formData.country || 'US',
      phone_main: formData.phone_main || null,
      email_general: formData.email_general || null,
      status: formData.status || 'active',
    })
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Update an existing location
 */
export async function updateLocation(
  id: string,
  formData: Partial<LocationFormData>
): Promise<LocationRow> {
  // Build update object, only including fields that were provided
  const updateData: Record<string, unknown> = {}

  if (formData.name !== undefined) updateData.name = formData.name
  if (formData.airport_code !== undefined)
    updateData.airport_code = formData.airport_code?.toUpperCase() || null
  if (formData.location_code !== undefined)
    updateData.location_code = formData.location_code?.toUpperCase()
  if (formData.iata_code !== undefined)
    updateData.iata_code = formData.iata_code?.toUpperCase() || null
  if (formData.latitude !== undefined) updateData.latitude = formData.latitude
  if (formData.longitude !== undefined) updateData.longitude = formData.longitude
  if (formData.timezone !== undefined) updateData.timezone = formData.timezone
  if (formData.address_line1 !== undefined)
    updateData.address_line1 = formData.address_line1 || null
  if (formData.city !== undefined) updateData.city = formData.city || null
  if (formData.state_province !== undefined)
    updateData.state_province = formData.state_province || null
  if (formData.postal_code !== undefined)
    updateData.postal_code = formData.postal_code || null
  if (formData.country !== undefined) updateData.country = formData.country || 'US'
  if (formData.phone_main !== undefined)
    updateData.phone_main = formData.phone_main || null
  if (formData.email_general !== undefined)
    updateData.email_general = formData.email_general || null
  if (formData.status !== undefined) updateData.status = formData.status

  const { data, error } = await supabase
    .from('locations')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Soft delete a location (sets deleted_at timestamp)
 */
export async function deleteLocation(id: string): Promise<void> {
  const { error } = await supabase
    .from('locations')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Restore a soft-deleted location
 */
export async function restoreLocation(id: string): Promise<void> {
  return restoreRecord('locations', id)
}
